/**
 * GetTheAppBanner — "download the app" prompt for mobile web visitors.
 *
 * Two complementary mechanisms (see also src/config/appStore.ts):
 *  1. Apple Smart App Banner — a <meta name="apple-itunes-app"> tag injected when an
 *     App Store ID is configured. iOS *Safari* renders its own native banner from it
 *     (and shows "Open" if the app is installed). We do NOT draw our own bar there.
 *  2. Custom banner (this component's UI) — for every OTHER iOS browser, especially the
 *     in-app webviews inside Instagram / Twitter / etc. where Safari's banner never
 *     appears. Brand-matched, dismissible.
 *
 * The custom pop-up is a centered modal with two variants, chosen by viewport size:
 *  - Mobile size (phones, narrow windows): a button straight to the App Store — you're
 *    already on the device, so no email step.
 *  - Desktop: you can't install an iPhone app on a laptop, so we capture an email and
 *    send the App Store link (POST /api/emails/app-link) to open on the phone.
 * iOS Safari instead gets its native Smart App Banner. Never in standalone/PWA mode,
 * never on the drawing or admin screens, and not for ~2 weeks after dismissal.
 *
 * Preview on any device (incl. desktop) with the query param ?appbanner=1.
 */

import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { APP_STORE_APP_ID, APP_STORE_URL, APP_NAME } from '../config/appStore';
import { sendAppLink } from '../services/email';

const DISMISS_KEY = 'pb_app_banner_dismissed_at';
const DISMISS_DAYS = 14;
const MONO = "'Delcan Mono', monospace"; // brand mono, registered in src/styles/globals.css

function isIOS(): boolean {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent;
  return (
    /iphone|ipad|ipod/i.test(ua) ||
    // iPadOS reports as Mac; disambiguate via touch points
    (navigator.platform === 'MacIntel' && (navigator as any).maxTouchPoints > 1)
  );
}

/** True only in genuine mobile Safari — which gets the native Smart App Banner instead. */
function isRealSafari(): boolean {
  const ua = navigator.userAgent;
  const inAppWebview =
    /(instagram|fban|fbav|fb_iab|line|micromessenger|twitter|gsa|pinterest|snapchat|tiktok|linkedin)/i.test(ua);
  const otherBrowser = /(crios|fxios|edgios|opios|chrome|android)/i.test(ua);
  return /safari/i.test(ua) && !inAppWebview && !otherBrowser;
}

function isStandalone(): boolean {
  return (
    (typeof window !== 'undefined' &&
      window.matchMedia &&
      window.matchMedia('(display-mode: standalone)').matches) ||
    (navigator as any).standalone === true
  );
}

/** A genuine desktop/laptop (mouse-driven) — not a phone or tablet. */
function isDesktop(): boolean {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent;
  if (/Mobi|Android|iPhone|iPad|iPod/i.test(ua)) return false;
  // iPadOS reports as Mac with touch — that's a tablet, handled by isIOS().
  if (navigator.platform === 'MacIntel' && (navigator as any).maxTouchPoints > 1) return false;
  return true;
}

function recentlyDismissed(): boolean {
  try {
    const v = localStorage.getItem(DISMISS_KEY);
    if (!v) return false;
    return Date.now() - Number(v) < DISMISS_DAYS * 86400000;
  } catch {
    return false;
  }
}

type SendStatus = 'idle' | 'sending' | 'sent' | 'error';

export function GetTheAppBanner() {
  const location = useLocation();
  const [dismissed, setDismissed] = useState<boolean>(() => recentlyDismissed());
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<SendStatus>('idle');
  const [errMsg, setErrMsg] = useState('');
  const [isMobileSized, setIsMobileSized] = useState<boolean>(
    () => typeof window !== 'undefined' && window.matchMedia('(max-width: 640px)').matches
  );

  // (1) Inject the Smart App Banner meta tag for Safari, once, when an ID is configured.
  useEffect(() => {
    if (!APP_STORE_APP_ID) return;
    if (document.querySelector('meta[name="apple-itunes-app"]')) return;
    const meta = document.createElement('meta');
    meta.name = 'apple-itunes-app';
    meta.content = `app-id=${APP_STORE_APP_ID}`;
    document.head.appendChild(meta);
  }, []);

  // Re-evaluate the mobile/desktop split when the viewport crosses the breakpoint.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mq = window.matchMedia('(max-width: 640px)');
    const onChange = () => setIsMobileSized(mq.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  const forcePreview =
    typeof window !== 'undefined' &&
    new URLSearchParams(window.location.search).has('appbanner');

  const routeBlocked =
    location.pathname.startsWith('/admin') || location.pathname === '/user-draw';

  // Mobile size → link straight to the App Store (you're already on the device, so no
  // email needed). Desktop → the "email me the link" flow. iOS Safari keeps its native
  // Smart App Banner. The width check is what makes a phone (or a narrow window) skip the
  // email step.
  const mode: 'direct' | 'email' | null = isIOS()
    ? isRealSafari()
      ? null
      : 'direct'
    : isMobileSized
      ? 'direct'
      : isDesktop()
        ? 'email'
        : null;

  const shouldShow =
    !!APP_STORE_URL &&
    mode !== null &&
    (forcePreview || (!dismissed && !routeBlocked && !isStandalone()));

  // A centered pop-up overlays the page (no content offset needed — unlike the old bar).
  if (!shouldShow) return null;

  const persist = () => {
    try {
      localStorage.setItem(DISMISS_KEY, String(Date.now()));
    } catch {
      /* ignore */
    }
  };
  const dismiss = () => {
    persist();
    setDismissed(true);
  };

  const emailOk = /.+@.+\..+/.test(email.trim());
  const submitEmail = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!emailOk || status === 'sending') return;
    setStatus('sending');
    const r = await sendAppLink(email.trim());
    if (r.ok) {
      persist(); // don't nag again, but keep the success message visible
      setStatus('sent');
    } else {
      setErrMsg(r.error || 'Something went wrong — try again.');
      setStatus('error');
    }
  };

  return (
    <div
      className="pb-app-backdrop fixed inset-0 z-[10001] flex items-center justify-center p-6"
      style={{ background: 'rgba(20,18,16,0.55)' }}
      onClick={dismiss}
    >
      <div
        className="pb-app-modal relative w-full max-w-sm rounded-[22px] border border-background/15 bg-foreground text-background p-6 text-left"
        role="dialog"
        aria-modal="true"
        aria-label="Get the Prompt-Brush app"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          aria-label="Dismiss"
          onClick={dismiss}
          className="absolute top-3 right-3 flex h-8 w-8 items-center justify-center rounded-full text-background/60 transition-colors hover:bg-background/10 hover:text-background"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
            <path d="M6 6l12 12M18 6L6 18" />
          </svg>
        </button>

        {/* Actual iOS app icon — neutral ring so the (dark) tile still reads on the
            inverted card, which is near-black in light mode. */}
        <span
          className="block h-[68px] w-[68px] overflow-hidden rounded-[16px]"
          style={{ boxShadow: '0 0 0 1.5px rgba(127,127,127,0.45)' }}
        >
          <img src="/app-icon.png" alt="" className="block h-full w-full" />
        </span>

        {mode === 'direct' ? (
          <>
            <h2 className="mt-4 text-[20px] leading-tight" style={{ fontFamily: MONO }}>
              {APP_NAME} is now an app
            </h2>
            <p className="mt-2 text-[13px] text-background/70" style={{ fontFamily: MONO }}>
              Draw the prompts by hand — no AI, just people.
            </p>
            <a
              href={APP_STORE_URL || '#'}
              target="_blank"
              rel="noopener noreferrer"
              onClick={dismiss}
              className="pb-glow-btn mt-5 block w-full rounded-full bg-background py-3.5 text-center text-[15px] text-foreground transition-opacity hover:opacity-80"
              style={{ fontFamily: MONO }}
            >
              Get it on the App Store
            </a>
            <button
              type="button"
              onClick={dismiss}
              className="mt-3 text-[13px] text-background/70 transition-opacity hover:opacity-100"
              style={{ fontFamily: MONO }}
            >
              Not now
            </button>
          </>
        ) : status === 'sent' ? (
          <>
            <h2 className="mt-4 text-[20px] leading-tight" style={{ fontFamily: MONO }}>
              Check your inbox
            </h2>
            <p className="mt-2 text-[13px] text-background/70" style={{ fontFamily: MONO }}>
              We sent the App Store link to <span className="text-background">{email.trim()}</span>. Open it on your iPhone to install {APP_NAME}.
            </p>
            <button
              type="button"
              onClick={dismiss}
              className="pb-glow-btn mt-5 block w-full rounded-full bg-background py-3.5 text-center text-[15px] text-foreground transition-opacity hover:opacity-80"
              style={{ fontFamily: MONO }}
            >
              Done
            </button>
          </>
        ) : (
          <>
            <h2 className="mt-4 text-[20px] leading-tight" style={{ fontFamily: MONO }}>
              {APP_NAME} is on iPhone
            </h2>
            <p className="mt-2 text-[13px] text-background/70" style={{ fontFamily: MONO }}>
              Enter your email and we'll send you the App Store link to open on your phone.
            </p>
            <form onSubmit={submitEmail}>
              <input
                type="email"
                inputMode="email"
                autoComplete="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  if (status === 'error') setStatus('idle');
                }}
                placeholder="you@email.com"
                className="mt-4 w-full rounded-full border border-background/25 bg-transparent px-4 py-3 text-[14px] text-background placeholder:text-background/50 outline-none focus:border-background/60"
                style={{ fontFamily: MONO }}
              />
              {status === 'error' && (
                <p className="mt-2 text-[12px]" style={{ fontFamily: MONO, color: '#ec6a60' }}>
                  {errMsg}
                </p>
              )}
              <button
                type="submit"
                disabled={!emailOk || status === 'sending'}
                className="pb-glow-btn mt-3 block w-full rounded-full bg-background py-3.5 text-center text-[15px] text-foreground transition-opacity hover:opacity-80 disabled:opacity-40"
                style={{ fontFamily: MONO }}
              >
                {status === 'sending' ? 'Sending…' : 'Email me the App Store link'}
              </button>
            </form>
            <button
              type="button"
              onClick={dismiss}
              className="mt-3 text-[13px] text-background/70 transition-opacity hover:opacity-100"
              style={{ fontFamily: MONO }}
            >
              Not now
            </button>
          </>
        )}
      </div>
    </div>
  );
}
